import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import ClassicRewardsSealIcon from 'LuxKit/Icons/loyalty/ClassicRewardsSealIcon'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import FormatCurrency from 'components/Common/FormatCurrency'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import { isRewardFareClass, type FareClassWithReward } from 'constants/flight'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useCharterFareClassPolicy from 'hooks/Flights/useCharterFareClassPolicy'
import useMaldivesCharterModalFareClasses from 'hooks/Flights/useMaldivesCharterModalFareClasses'
import useCheapestAvailableReward from 'hooks/OfferPage/useCheapestAvailableReward'
import useOpenCharterFareClassModal from 'hooks/OfferPage/useOpenCharterFareClassModal'

const Card = styled(Pane)`
  text-align: center;
`

interface MappedProps {
  offer?: App.Offer
  fareClass: FareClassWithReward
  checkInDate?: string
  checkOutDate?: string
  occupancies: Array<App.Occupants>
}

/**
 * Charter-bundle upsell shown when the member is browsing a non-reward fare
 * but has enough Société points to afford at least one of the offer's
 * available reward fare classes. Clicking "View package" reopens the
 * "Select a class" modal with the current fare preselected so they can
 * switch into a reward redemption.
 */
function FlashHotelRewardsAvailableCard({ offer, fareClass, checkInDate, checkOutDate, occupancies }: MappedProps) {
  const intl = useIntl()
  const loyaltyProgramName = getLoyaltyProgramDisplayName()

  const policy = useCharterFareClassPolicy(offer, occupancies)
  const allowedFareClasses = useMaldivesCharterModalFareClasses(policy.allowedFareClasses)

  const { cheapest } = useCheapestAvailableReward({
    checkInDate,
    checkOutDate,
    occupancies,
    allowedFareClasses,
  })

  const { open } = useOpenCharterFareClassModal({ offer, fareClass, checkInDate, checkOutDate, occupancies })

  if (isRewardFareClass(fareClass)) return null
  if (!cheapest) return null

  return (
    <Card background="normal-background" border="light-foreground" borderRadius="M" padding={10}>
      <Group direction="vertical" gap={8}>
        <Group direction="horizontal" verticalAlign="center" horizontalAlign="center" gap={4}>
          <ClassicRewardsSealIcon size={14} colour="highlight-primary-dark" aria-hidden />
          <BodyText variant="small" weight="bold" colour="highlight-primary-dark" as="span" wrap="no-wrap">
            {intl.formatMessage(
              {
                id: '_B_goO2IWUjxHS-SrF0H',
                defaultMessage: '{loyaltyProgramName} Rewards available',
                description: 'Heading of the rewards-available upsell card on the Flash hotel offer page; loyaltyProgramName is the configured program (e.g. Société).',
              },
              { loyaltyProgramName },
            )}
          </BodyText>
        </Group>
        <BodyText variant="small" colour="neutral-one">
          <FormattedMessage
            id="M8VSbNAo77HYZUM6bz1E"
            defaultMessage="from {points} + {cash}"
            description="From-price line on the rewards-available upsell card on the Flash hotel offer page; shows the cheapest available reward redemption as points + cash co-pay per person."
            values={{
              points: <FormatLoyaltyPoints points={cheapest.pointsPerPerson} abbreviated />,
              cash: <FormatCurrency value={cheapest.cashPerPerson} format="roundedDollar" />,
            }}
          />
        </BodyText>
        <TextLink size="small" weight="bold" onClick={() => open()}>
          {intl.formatMessage({
            id: 'lg7A6gOMqL3-7HZHQKIc',
            defaultMessage: 'View package',
            description: 'CTA on the rewards-available upsell card on the Flash hotel offer page; opens the "Select a class" modal preselected on the current fare so members can pick the reward variant.',
          })}
        </TextLink>
      </Group>
    </Card>
  )
}

export default withOfferPageState((state): MappedProps => ({
  offer: state.offer,
  fareClass: state.fareClass,
  checkInDate: state.checkInDate,
  checkOutDate: state.checkOutDate,
  occupancies: state.rooms,
}), FlashHotelRewardsAvailableCard)
